'use client'

import { useActionToast } from '@mentora/app-shared/i18n/use-action-toast'
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
} from '@mentora/ui-components'
import { Loader2 } from 'lucide-react'
import { useFormatter, useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { useState, useTransition } from 'react'
import { toast } from 'sonner'
import { callApi } from '../api-client'

// The buyer's refund runs on apps/api, beside the payment it returns
// (`/api/orders/refund`, through the web app's rewrite). It is not a server
// action, so the screen is refreshed by hand once it succeeds.

export function CancelOrderDialog({ enrollmentId, deadline }: {
  enrollmentId: string
  deadline: Date
}) {
  const router = useRouter()
  const t = useTranslations('orders')
  const format = useFormatter()
  const { showError } = useActionToast()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [isPending, startTransition] = useTransition()

  function handleConfirm() {
    startTransition(async () => {
      const result = await callApi<{ ok: true }>('/api/orders/refund', { method: 'POST', body: { enrollmentId, reason: reason.trim() || undefined } }, 'orders.errors.refundFailed')
      if (!result.ok) {
        showError(result)
        return
      }
      router.refresh()
      toast.success(t('toast.refunded'))
      setOpen(false)
      setReason('')
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} disabled={isPending}>
        {t('cancelButton')}
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('dialog.title')}</DialogTitle>
          <DialogDescription>
            {t('dialog.description', {
              date: format.dateTime(deadline, { year: 'numeric', month: 'long', day: 'numeric' }),
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor={`reason-${enrollmentId}`}>{t('dialog.reasonLabel')}</Label>
          <Textarea
            id={`reason-${enrollmentId}`}
            value={reason}
            onChange={e => setReason(e.target.value)}
            maxLength={500}
            placeholder={t('dialog.reasonPlaceholder')}
            rows={3}
          />
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" disabled={isPending}>{t('dialog.cancel')}</Button>
          </DialogClose>
          <Button onClick={handleConfirm} disabled={isPending}>
            {isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
            {t('dialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
